import EXIF from 'exif-js'

export const rotateImage = async (image: HTMLImageElement): Promise<string> => {
  let orientation = await getImageOrientation(image)
  let tmpCanvas = document.createElement('canvas')
  if (image.width >= image.height) {
    let ratio = 750 / image.height
    tmpCanvas.height = 750
    tmpCanvas.width = image.width * ratio
  } else {
    let ratio = 750 / image.width
    tmpCanvas.width = 750
    tmpCanvas.height = image.height * ratio
  }
  if (orientation === 6 || orientation === 8) {
    //交换画布宽高
    tmpCanvas.width += tmpCanvas.height
    tmpCanvas.height = tmpCanvas.width - tmpCanvas.height
    tmpCanvas.width -= tmpCanvas.height
  }
  let ctx = tmpCanvas.getContext('2d')!
  switch (orientation) {
    case 1:
      //无需旋转
      ctx.drawImage(image, 0, 0, tmpCanvas.width, tmpCanvas.height)
      return tmpCanvas.toDataURL('image/jpeg')
    case 3:
      //需要旋转180度
      ctx.translate(tmpCanvas.width, tmpCanvas.height)
      ctx.rotate(Math.PI)
      ctx.drawImage(image, 0, 0, tmpCanvas.width, tmpCanvas.height)
      return tmpCanvas.toDataURL('image/jpeg')
    case 6:
      //需要顺时针旋转90°
      ctx.translate(tmpCanvas.width, 0)
      ctx.rotate((90 * Math.PI) / 180)
      ctx.drawImage(image, 0, 0, tmpCanvas.height, tmpCanvas.width)
      return tmpCanvas.toDataURL('image/jpeg')
    case 8:
      //需要顺时针270°
      ctx.translate(0, tmpCanvas.height)
      ctx.rotate((270 * Math.PI) / 180)
      ctx.drawImage(image, 0, 0, tmpCanvas.height, tmpCanvas.width)
      return tmpCanvas.toDataURL('image/jpeg')
    default:
      //无需旋转
      ctx.drawImage(image, 0, 0, tmpCanvas.width, tmpCanvas.height)
      return tmpCanvas.toDataURL('image/jpeg')
  }
}

/**
 * 从File获取dataURL并旋转
 * @param file
 * @returns {Promise<*>}
 */
export const rotateImageFile = async (file: File): Promise<string> => {
  let orientation = await getImageOrientation(file)
  let dataURL = await readFileAsDataUrl(file)
  let canvas = document.createElement('canvas')
  let image = await createImageWithDataURL(dataURL as string) as HTMLImageElement
  let ctx = canvas.getContext('2d')!
  if (image.width >= image.height) {
    let ratio = 750 / image.height
    canvas.height = 750
    canvas.width = image.width * ratio
  } else {
    let ratio = 750 / image.width
    canvas.width = 750
    canvas.height = image.height * ratio
  }
  if (orientation === 6 || orientation === 8) {
    //交换画布宽高
    canvas.width += canvas.height
    canvas.height = canvas.width - canvas.height
    canvas.width -= canvas.height
  }
  switch (orientation) {
    case 1:
      //无需旋转
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
      return canvas.toDataURL('image/jpeg')
    case 3:
      //需要旋转180度
      ctx.translate(canvas.width, canvas.height)
      ctx.rotate(Math.PI)
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
      return canvas.toDataURL('image/jpeg')
    case 6:
      //需要顺时针旋转90°
      ctx.translate(canvas.width, 0)
      ctx.rotate((90 * Math.PI) / 180)
      ctx.drawImage(image, 0, 0, canvas.height, canvas.width)
      return canvas.toDataURL('image/jpeg')
    case 8:
      //需要顺时针270°
      ctx.translate(0, canvas.height)
      ctx.rotate((270 * Math.PI) / 180)
      ctx.drawImage(image, 0, 0, canvas.height, canvas.width)
      return canvas.toDataURL('image/jpeg')
    default:
      //无需旋转
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
      return canvas.toDataURL('image/jpeg')
  }
}

/**
 * 从File读取dataURL
 * @param file
 * @returns {Promise<any>}
 */
export const readFileAsDataUrl = (file: File): Promise<string | ArrayBuffer | null> => {
  const reader = new FileReader()
  reader.readAsDataURL(file)
  return new Promise((resolve, reject) => {
    reader.onload = (e) => {
      resolve(e.target!.result)
    }
    reader.onerror = (err) => {
      reject(err)
    }
  })
}

/**
 * 根据dataURL创建Image
 * @param dataURL
 * @returns {Promise<*>}
 */
export const createImageWithDataURL = (dataURL) => {
  const image = new Image()
  image.setAttribute('crossOrigin', 'Anonymous')
  image.src = dataURL
  return new Promise((resolve) => {
    image.onload = () => resolve(image)
  })
}

/**
 * 根据dataURL创建Image, 不设置CORS
 * @param dataURL
 * @returns {Promise<*>}
 */
export const createImageWithDataURLWithoutCORS = (dataURL) => {
  const image = new Image()
  image.src = dataURL
  return new Promise((resolve) => {
    image.onload = () => resolve(image)
  })
}

/**
 * 获取图片方向
 * @param image
 * @returns {Promise<any>}
 */
export const getImageOrientation = (image) => {
  return new Promise((resolve) => {
    EXIF.getData(image, () => {
      let orientation = EXIF.getTag(image, 'Orientation')
        ? EXIF.getTag(image, 'Orientation')
        : 1
      resolve(orientation)
    })
  })
}

/**
 * 将base64字符转换为Blob对象
 * @param b64Data
 * @param contentType
 * @param sliceSize
 * @returns {Blob}
 */
export const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512): Blob => {
  const byteCharacters = atob(b64Data)
  const byteArrays: BlobPart[] = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)

    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentType })
}

/**
 * 将DataUrl转为Blob对象
 * @param dataURL
 * @returns {Blob}
 */
export const dataURLtoBlob = (dataURL) => {
  let chunks = dataURL.split(';')
  return b64toBlob(chunks[1].split(',')[1], chunks[0].split(':')[1])
}

export const drawRoundedRect = (ctx, x, y, width, height, r, fill, stroke) => {
  ctx.save()
  ctx.beginPath() // draw top and top right corner
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + width, y, x + width, y + r, r) // draw right side and bottom right corner
  ctx.arcTo(x + width, y + height, x + width - r, y + height, r) // draw bottom and bottom left corner
  ctx.arcTo(x, y + height, x, y + height - r, r) // draw left and top left corner
  ctx.arcTo(x, y, x + r, y, r)
  if (fill) {
    ctx.fill()
  }
  if (stroke) {
    ctx.stroke()
  }
  ctx.restore()
}

/**
 * 将组件坐标转换为基于原始图片尺寸的坐标
 * @param {Object} componentRect - 组件上的矩形坐标 {x, y, width, height}
 * @param {Object} displaySize - 图片在容器中的显示尺寸 {width, height}
 * @param {Object} originalSize - 图片的原始尺寸 {width, height}
 * @returns {Object} 基于原始图片尺寸的坐标 {x, y, width, height}
 */
export const convertComponentToOriginalCoords = (
  componentRect,
  displaySize,
  originalSize
) => {
  // 计算缩放比例
  const scaleX = originalSize.width / displaySize.width
  const scaleY = originalSize.height / displaySize.height

  return {
    x: Math.round(componentRect.x * scaleX),
    y: Math.round(componentRect.y * scaleY),
    width: Math.round(componentRect.width * scaleX),
    height: Math.round(componentRect.height * scaleY)
  }
}

/**
 * 计算图片在容器中的显示尺寸（保持宽高比）
 * @param {Object} originalSize - 图片的原始尺寸 {width, height}
 * @param {number} maxWidth - 容器的最大宽度
 * @param {number} maxHeight - 容器的最大高度（可选）
 * @returns {Object} 显示尺寸 {width, height}
 */
export const calculateImageDisplaySize = (
  originalSize: { width: number; height: number },
  maxWidth: number,
  maxHeight: number | null = null
): { width: number; height: number } => {
  let displayWidth = originalSize.width
  let displayHeight = originalSize.height

  // 优先按宽度缩放
  if (displayWidth > maxWidth) {
    const widthRatio = maxWidth / displayWidth
    const scaledWidth = maxWidth
    const scaledHeight = displayHeight * widthRatio

    // 检查按宽度缩放后的高度是否超过最大高度
    if (maxHeight && scaledHeight > maxHeight) {
      // 如果超过最大高度，则改为按高度缩放
      const heightRatio = maxHeight / displayHeight
      displayHeight = maxHeight
      displayWidth = displayWidth * heightRatio
    } else {
      // 如果没有超过最大高度，使用宽度缩放结果
      displayWidth = scaledWidth
      displayHeight = scaledHeight
    }
  } else if (maxHeight && displayHeight > maxHeight) {
    // 如果宽度不超过但高度超过，按高度缩放
    const ratio = maxHeight / displayHeight
    displayHeight = maxHeight
    displayWidth = displayWidth * ratio
  }

  return {
    width: Math.round(displayWidth),
    height: Math.round(displayHeight)
  }
}

/**
 * 优化阿里云图片URL，添加宽度参数以节省流量
 * @param {string} imageUrl - 原始图片URL
 * @param {number} width - 目标宽度，默认400
 * @returns {string} 优化后的图片URL
 */
export const optimizeAliyunImageUrl = (imageUrl, width = 400) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return imageUrl
  }

  // 检查是否是阿里云OSS图片URL
  if (!imageUrl.includes('net-cloud.xhey.top')) {
    return imageUrl
  }

  // 如果URL已经包含处理参数，先移除
  const baseUrl = imageUrl.split('?')[0]

  // 添加阿里云图片处理参数：调整宽度
  return `${baseUrl}?x-oss-process=image/resize,w_${width}`
}
